/**
 * BrowserRouter cannot use useBlocker (data router only). We add a duplicate
 * history entry so the first "back" hits popstate while staying on the same URL,
 * then re-push if the user cancels.
 *
 * Idempotent: checks window.history.state instead of a module-level flag so the
 * trap can be safely reinstalled after every in-app route change.
 */
export function ensureHistoryBackTrap(): void {
  if (typeof window === "undefined") return;
  if (window.history.state?.__mgmNavGuardTrap) return;
  window.history.pushState(
    { __mgmNavGuardTrap: true },
    "",
    window.location.href,
  );
}

/**
 * Call immediately before programmatic history moves (e.g. `navigate(-1)`,
 * `history.back()`) so the navigation guard does not treat the resulting
 * `popstate` as the browser Back control.
 *
 * If you use `history.go(-k)` and the runtime emits multiple `popstate`
 * events, pass `k` or call once per expected pop.
 */
let programmaticPopstateSuppressions = 0;

export function suppressNextHistoryGuardPrompt(steps = 1): void {
  programmaticPopstateSuppressions += Math.max(1, Math.floor(steps));
}

/** @internal — used by useGlobalNavigationGuard */
export function consumeHistoryGuardProgrammaticPopSuppression(): boolean {
  if (programmaticPopstateSuppressions <= 0) return false;
  programmaticPopstateSuppressions -= 1;
  return true;
}
